import { AxiosError, isAxiosError } from "axios";

const extractAxiosMessage = (error: AxiosError): string => {
  const status = error.response?.status;
  const statusPrefix = status === undefined ? "" : `[${status}] `;
  const data = error.response?.data;
  if (typeof data === "string" && data.length > 0) {
    return `${statusPrefix}${data}`;
  }
  if (data && typeof data === "object") {
    const dataMessage = (data as { message?: unknown }).message;
    if (typeof dataMessage === "string" && dataMessage.length > 0) {
      return `${statusPrefix}${dataMessage}`;
    }
  }
  return `${statusPrefix}${error.message}`;
};

const stringifyObject = (value: object): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return "Unknown error";
  }
};

/**
 * Narrows a caught `unknown` into a human-readable string.
 *
 * Frontend-safe equivalent of `shared/src/lib/logHelper.ts`'s `toErrorMessage`.
 * The backend version pulls in `@aws-lambda-powertools/logger`, so it can't be
 * imported into bcc/client/client-shared bundles.
 *
 * Resolution order:
 *   1. string                  → returned as-is
 *   2. axios error             → `[status] ` prefix (when present) plus the
 *                                response body if it's a non-empty string,
 *                                else `data.message` if present, else
 *                                `error.message`. Other object-shaped bodies
 *                                are not stringified.
 *   3. Error                   → `.message`
 *   4. plain object            → JSON.stringify (or "Unknown error" on circular)
 *   5. anything else           → "Unknown error"
 */
export const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }
  if (isAxiosError(error)) {
    return extractAxiosMessage(error);
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === "object") {
    return stringifyObject(error);
  }
  return "Unknown error";
};
